/**
 * Mobile Navigation Component
 * 
 * Provides a responsive navigation menu for mobile devices.
 */

import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router';
import { Menu, X, FileText, Plus, Archive, User, Briefcase } from 'lucide-react';
import { useUser } from '../lib/userContext';

export function MobileNav() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { currentUser } = useUser();
  
  const isActive = (path: string) => location.pathname === path;
  
  const navItems = useMemo(() => [
    { path: '/dashboard', label: 'Dashboard', icon: FileText },
    { path: '/dashboard/create', label: 'Create Report', icon: Plus },
    ...(currentUser.role === 'manager' ? [
      { path: '/dashboard/projects', label: 'Projects', icon: Briefcase },
      { path: '/dashboard/users', label: 'Users', icon: User },
    ] : []),
    { path: '/dashboard/archive', label: 'Archive', icon: Archive },
  ], [currentUser.role]);
  
  // Close menu on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="lg:hidden p-2 text-neutral-700 hover:bg-neutral-100 rounded-lg"
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={isOpen}
      >
        {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>
      
      {/* Mobile Menu Overlay */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          
          {/* Menu Panel */}
          <nav 
            className="lg:hidden fixed top-[73px] left-0 right-0 bottom-0 bg-white z-50 overflow-y-auto"
            role="navigation"
            aria-label="Mobile navigation"
          >
            <div className="p-4 space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    aria-current={active ? 'page' : undefined}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      active
                        ? 'bg-orange-100 text-orange-900'
                        : 'text-neutral-700 hover:bg-neutral-100'
                    }`}
                  >
                    <Icon className="h-5 w-5" aria-hidden="true" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </nav>
        </>
      )}
    </>
  );
}

export default MobileNav;
